import React, { useState } from "react";
import { Checkbox, FormControlLabel, Tooltip } from "@mui/material";
import HelpIcon from "@mui/icons-material/Help";
import { useTranslation } from "react-i18next";
import DebouncedNumericPicker from "../../../../common/components/UI/DebouncedNumericPicker";
import ReactToggle from '../../../../common/components/UI/ToggleSwitch/ReactToggle';
import { HoursMinutesPickerStyles } from "../../../Admin/components/Form/Form.service";

import './RequestPeriodLimits.scss';

const noop = () => {};

/**
 * @typedef {{
 *  requestPeriodLimit: number|null;
 *  hidden?: boolean;
 *  disabled?: boolean;
 *  classes?: Object;
 *  isShowHideRequestsOutsideRequestPeriod?: boolean;
 *  isHideRequestsOutsideRequestPeriod?: boolean;
 *  onChange?: (limit: number) => void;
 *  onChangeToggle?: (isChecked: boolean) => void;
 *  onChangeHideRequestsOutsideRequestPeriod?: (isChecked: boolean) => void;
 * }} RequestPeriodLimitsProps
 */


/** @param {RequestPeriodLimitsProps} param0 */
const RequestPeriodLimits = ({
    requestPeriodLimit: initialPeriodLimit,
    hidden,
    disabled,
    classes = {},
    isShowHideRequestsOutsideRequestPeriod = false,
    isHideRequestsOutsideRequestPeriod = false,
    onChange = noop,
    onChangeToggle: onChangeToggleHandler = noop,
    onChangeHideRequestsOutsideRequestPeriod = noop,
}) => {
    const { t } = useTranslation('employmentBillboard', { keyPrefix: "form" });
    
    // Guards
    initialPeriodLimit = initialPeriodLimit ?? null;
    hidden = hidden ?? false;
    disabled = disabled ?? false;
    onChange = typeof onChange !== "function" ? noop: onChange;

    const [isShowRequestPeriodLimits, setIsShowRequestPeriodLimits] = useState(Number.isInteger(initialPeriodLimit));

    /**
     * @param {boolean} checked 
     */
    const onChangeToggle = (checked) => {
        setIsShowRequestPeriodLimits(checked);
        onChangeToggleHandler(checked);
    };

    /**
     * @param {number} limit 
     */
    const onChangeRequestPeriodLimit = (limit) => {
        onChange(limit);
    };

    return (
        <div className="request-period-limits">
            <ReactToggle
                ToggleFct={onChangeToggle}
                labelOn={t('requestPeriodLimits.title')}
                labelOff={t('requestPeriodLimits.title')}
                note=""
                toggleValue={isShowRequestPeriodLimits}
                hidden={hidden}
                disabled={disabled}
            />
            {
                isShowRequestPeriodLimits && (
                    <>
                        <div className="container-numeric-picker">
                            <DebouncedNumericPicker
                                min={1}
                                max={90}
                                disabled={!isShowRequestPeriodLimits || disabled}
                                value={initialPeriodLimit}
                                onChange={onChangeRequestPeriodLimit}
                                classes={{
                                    ...classes,
                                    container: classes.numberPickers,
                                }}
                                style={{
                                    ...HoursMinutesPickerStyles,
                                    input: {
                                        ...HoursMinutesPickerStyles.input,
                                        maxWidth: 64,
                                }}}
                            />
                            <span>{t('days')}</span>
                        </div>
                        <span>
                            <small className="disclaimer">{t('requestPeriodLimits.disclaimer')}</small>
                        </span>
                        {
                            isShowHideRequestsOutsideRequestPeriod && (
                                <div className="hide-requests-outside-request-period">
                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                checked={isHideRequestsOutsideRequestPeriod}
                                                disabled={disabled}
                                                onChange={({ target: { checked } }) => onChangeHideRequestsOutsideRequestPeriod(checked)}
                                            />
                                        )}
                                        label={t('requestPeriodLimits.hideRequestsOutsideRequestPeriod')}
                                    />
                                    <Tooltip
                                        title={t('requestPeriodLimits.hideRequestsOutsideRequestPeriodTooltip')}
                                        arrow
                                    >
                                        <HelpIcon fontSize="small" />
                                    </Tooltip>
                                </div>
                            )
                        }
                    </>
                )
            }
        </div>
    );
};

export default RequestPeriodLimits;
